import React from 'react';
import { shouldUseUnifyTheme } from 'theme/unifyTheme';

import useScreen from '@lyearn/core/hooks/useScreen';
import platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { Logo } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { Link } from '@/components/Link';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';

import { logoStyles } from './styles';
import type { LeftNodeProps } from './types';

function DefaultLeftNode(props: LeftNodeProps) {
  const { t } = useTranslation();
  const { textColor, logoPreviewUrl } = props;
  const {
    [FeatureFlags.EnableUnifiedDashboard]: isUnifiedDashboardEnabled,
    [FeatureFlags.ShowUserProfile]: showUserProfile,
    [FeatureFlags.DefaultRestricted]: isDefaultRestricted,
  } = useFeatureFlags();
  const {
    site: { logoUrl, appUrl, appUrlTarget, name },
  } = useAppConfig();
  const { isMobile } = useScreen();

  // Logo sizing: bump the logo box only for the ainw (Unify) instance; stock for all others.
  const isUnify = shouldUseUnifyTheme();
  const logoMaxWidth = isUnify ? 'max-w-[18rem]' : 'max-w-[13rem]';
  const baseLogoHeight = isUnifiedDashboardEnabled ? 'h-44' : isDefaultRestricted ? 'h-32' : 'h-24';
  const unifyLogoHeight = isUnifiedDashboardEnabled
    ? 'h-[4.8rem]'
    : isDefaultRestricted
    ? 'h-[4rem]'
    : 'h-[3.2rem]';
  const logoHeight = isUnify ? unifyLogoHeight : baseLogoHeight;

  if (!showUserProfile) return null;

  const LogoImage = () => {
    return logoUrl ? (
      <Image
        useImg
        alt={t('Common:LogoOfImage', { imageAlt: name })}
        className={cn(logoMaxWidth, logoHeight)}
        resizeMode="contain"
        src={logoPreviewUrl || logoUrl}
        // need style prop to work in native
        style={
          platform.OS !== 'web' ? { height: 24, width: 100, alignItems: 'flex-start' } : undefined
        }
      />
    ) : (
      <Logo
        className={cn(
          'transition duration-300 ease-in-out',
          textColor,
          logoStyles,
          !isUnifiedDashboardEnabled ? 'h-24' : 'h-44',
        )}
        fill="currentColor"
      />
    );
  };

  const LogoComponent = appUrl ? (
    <a
      aria-label={t('Common:GoToHome', { appName: name })}
      href={appUrl}
      rel="noreferrer"
      target={appUrlTarget}>
      <LogoImage />
    </a>
  ) : (
    <Link aria-label={t('Common:GoToHome', { appName: name })} to="/">
      <LogoImage />
    </Link>
  );

  return (
    <Box
      aria-label={t('Common:Home')}
      className={cn(isUnifiedDashboardEnabled ? '-ms-6 xs:ms-6' : 'ps-24 xs:ps-24')}
      component="nav">
      {LogoComponent}
    </Box>
  );
}

export default React.memo(DefaultLeftNode);
